"use client";

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ImagePlusIcon } from "lucide-react";
import { toast } from "sonner";
import { DatePager } from "@/components/date-pager";
import { DiaryPhotoBubbles, DiaryTaskList, uniqueHomeworkPhotos } from "@/components/diary-task-line";
import { HomeworkCapturePanel } from "@/components/homework-capture";
import { HomeworkEditDialog } from "@/components/homework-edit-dialog";
import { useLiveReload } from "@/components/live-provider";
import { useMeActions, useMeContext } from "@/components/me-provider";
import { TimetableEditor } from "@/components/timetable-editor";
import { ZoomPhotoDialog } from "@/components/zoom-photo-dialog";
import { SmoothReveal } from "@/components/smooth-reveal";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { addYmd, formatYmdRange, kyivDate, kyivTime } from "@/lib/kyiv";
import { isStaff } from "@/lib/roles";
import { computeNow, compactHhmm, shortSubject, WEEKDAY_SHORT } from "@/lib/timetable-core";
import type { DayLessonDto, HomeworkTaskDto, LessonDto, LessonNow, TimetableResponse } from "@/lib/types";
import { cn } from "@/lib/utils";

function compactTodayLine(now: LessonNow | null, until: string | null): string | null {
  const untilPart = until ? `уроки до ${compactHhmm(until)}` : null;
  if (!now || now.kind === "off") return untilPart;
  if (now.kind === "done") return until ? `уроки вже скінчились · було до ${compactHhmm(until)}` : "Уже кінець";
  if (now.kind === "lesson") {
    const current = `Зараз ${now.slotOrder}. ${now.title} · до ${compactHhmm(now.end)}`;
    return untilPart ? `${current} · ${untilPart}` : current;
  }
  if (now.kind === "before") {
    return [`${now.slotOrder}. ${now.title} з ${compactHhmm(now.start)}`, untilPart].filter(Boolean).join(" · ");
  }
  if (now.kind === "break") {
    return [`Далі ${now.slotOrder}. ${now.title} з ${compactHhmm(now.start)}`, untilPart].filter(Boolean).join(" · ");
  }
  return untilPart;
}

function dayChipClass(date: string, today: string, selected: string) {
  if (date === selected) return "bg-primary text-primary-foreground";
  if (date > today) return "bg-primary/12 text-primary";
  return "bg-muted font-medium text-muted-foreground";
}

function focusDateForWeek(week: Array<{ date: string }>, today: string) {
  const from = week[0]?.date;
  const to = week[week.length - 1]?.date;
  if (from && to && today >= from && today <= to) return today;
  return from ?? today;
}

function newestFirst(a: HomeworkTaskDto, b: HomeworkTaskDto) {
  if (a.createdAt !== b.createdAt) return b.createdAt.localeCompare(a.createdAt);
  return (a.slotOrder ?? 99) - (b.slotOrder ?? 99);
}

function attachTasks(lessons: DayLessonDto[], tasks: HomeworkTaskDto[]) {
  const remaining = [...tasks].sort(newestFirst);
  const byLesson = lessons.map((lesson) => {
    const matches: HomeworkTaskDto[] = [];
    for (let i = remaining.length - 1; i >= 0; i -= 1) {
      const task = remaining[i];
      if (task.subject !== lesson.title || task.slotOrder !== lesson.slotOrder) continue;
      matches.unshift(task);
      remaining.splice(i, 1);
    }
    matches.sort(newestFirst);
    return { lesson, tasks: matches };
  });
  for (const row of byLesson) {
    for (let i = remaining.length - 1; i >= 0; i -= 1) {
      const task = remaining[i];
      if (task.subject !== row.lesson.title || task.slotOrder != null) continue;
      row.tasks.push(task);
      remaining.splice(i, 1);
    }
    row.tasks.sort(newestFirst);
  }
  remaining.sort(newestFirst);
  return { byLesson, extra: remaining };
}

export function TimetableCard({ active = true }: { active?: boolean }) {
  const me = useMeContext();
  const { markSeen } = useMeActions();
  const staff = isStaff(me.user.role);
  const [payload, setPayload] = useState<TimetableResponse | null>(null);
  const [tasks, setTasks] = useState<HomeworkTaskDto[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [nowTick, setNowTick] = useState(() => kyivTime());
  const [editing, setEditing] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [openedPhoto, setOpenedPhoto] = useState<{
    photos: Array<{ url: string; createdAt?: string; authorName?: string }>;
    index: number;
    alt: string;
  } | null>(null);
  const [editingTask, setEditingTask] = useState<HomeworkTaskDto | null>(null);
  const [addingLesson, setAddingLesson] = useState<DayLessonDto | null>(null);
  const [weekOpen, setWeekOpen] = useState(false);

  async function load(date?: string, mode: "day" | "week" = "day") {
    const query = date ? `?date=${encodeURIComponent(date)}` : "";
    const data = await api<TimetableResponse>(`/api/timetable${query}`);
    if (mode === "week") {
      const focus = focusDateForWeek(data.week, data.today.date);
      if (focus !== data.day.date) {
        await load(focus, "day");
        return;
      }
    }
    setPayload(data);
    const from = data.week[0]?.date;
    const to = data.week[data.week.length - 1]?.date;
    const taskQuery =
      from && to
        ? `/api/homework/tasks?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`
        : "/api/homework/tasks?upcoming=1";
    const [homework, assigned] = await Promise.all([
      api<{ tasks: HomeworkTaskDto[] }>(taskQuery),
      api<{ tasks: HomeworkTaskDto[] }>("/api/homework/tasks?created=1"),
    ]);
    const byId = new Map<string, HomeworkTaskDto>();
    for (const task of [...homework.tasks, ...assigned.tasks]) byId.set(task.id, task);
    setTasks([...byId.values()]);
    setSelectedDate((current) => (date ? data.day.date : (current ?? data.day.date)));
  }

  useEffect(() => {
    void load().catch((error) => {
      toast.error(error instanceof Error ? error.message : "Не вдалося завантажити щоденник.");
    });
  }, []);

  useLiveReload("timetable", () => load(selectedDate ?? undefined), active);
  useLiveReload("homework", () => load(selectedDate ?? undefined), active);

  useEffect(() => {
    if (!active) return;
    void markSeen("homework");
  }, [active, tasks, markSeen]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(kyivTime()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!openedPhoto) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpenedPhoto(null);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openedPhoto]);

  const now = useMemo(() => {
    if (!payload) return null;
    return computeNow(payload.today.lessons, nowTick, payload.today.schoolDay);
  }, [payload, nowTick]);

  const day = payload?.day;
  const dayTasks = useMemo(
    () => (day ? tasks.filter((task) => task.dueDate === day.date) : []),
    [tasks, day],
  );
  const attached = useMemo(() => (day ? attachTasks(day.lessons, dayTasks) : { byLesson: [], extra: [] }), [day, dayTasks]);
  const dayPhotos = useMemo(() => uniqueHomeworkPhotos(dayTasks), [dayTasks]);
  const homeworkDates = useMemo(() => new Set(tasks.map((task) => task.dueDate)), [tasks]);
  const assignedToday = useMemo(() => {
    const today = payload?.today.date ?? kyivDate();
    return tasks.filter((task) => kyivDate(new Date(task.createdAt)) === today);
  }, [tasks, payload?.today.date]);

  if (!payload || !day) return null;

  function openDayPhoto(url: string, alt = "Фото з дошки") {
    const index = dayPhotos.findIndex((photo) => photo.url === url);
    setOpenedPhoto({
      photos: dayPhotos.map((photo) => ({
        url: photo.url,
        createdAt: photo.createdAt,
        authorName: photo.authorName,
      })),
      index: index >= 0 ? index : 0,
      alt,
    });
  }

  const currentSlot = now && (now.kind === "lesson" || now.kind === "before" || now.kind === "break") ? now.slotOrder : null;
  const isToday = day.date === payload.today.date;
  const weekFrom = payload.week[0]?.date;
  const weekTo = payload.week[payload.week.length - 1]?.date;
  const pagerLabel = weekFrom && weekTo ? formatYmdRange(weekFrom, weekTo) : day.date;
  const pagerHint =
    compactTodayLine(now, payload.today.until) ??
    (payload.today.schoolDay ? null : "Сьогодні уроків немає");

  return (
    <section className="space-y-3 rounded-3xl bg-card p-4 ring-1 ring-foreground/8">
      {staff ? (
        <Button type="button" className="h-11 w-full rounded-2xl" onClick={() => setAssignOpen(true)}>
          <ImagePlusIcon />
          {assignedToday.length > 0 ? "Оновити ДЗ" : "Задати ДЗ"}
        </Button>
      ) : null}

      <DatePager
        label={pagerLabel}
        hint={pagerHint}
        prevDate={weekFrom ? addYmd(weekFrom, -7) : null}
        nextDate={weekFrom ? addYmd(weekFrom, 7) : null}
        prevLabel="Попередній тиждень"
        nextLabel="Наступний тиждень"
        onChange={(date) => {
          setSelectedDate(date);
          void load(date, "week").catch((error) =>
            toast.error(error instanceof Error ? error.message : "Не вдалося відкрити тиждень."),
          );
        }}
      />

      <div className="flex gap-1">
        {payload.week.map((item) => (
          <button
            type="button"
            key={item.date}
            className={cn(
              "relative min-w-0 flex-1 rounded-2xl py-2.5 text-center text-[11px] font-semibold",
              dayChipClass(item.date, payload.today.date, day.date),
              !item.schoolDay && "opacity-45",
            )}
            onClick={() => {
              setSelectedDate(item.date);
              void load(item.date).catch((error) => toast.error(error instanceof Error ? error.message : "Не вдалося відкрити день."));
            }}
          >
            {WEEKDAY_SHORT[item.weekday]}
            {homeworkDates.has(item.date) ? (
              <span
                className={cn(
                  "mx-auto mt-1 block size-1.5 rounded-full",
                  item.date === day.date
                    ? "bg-primary-foreground"
                    : item.date > payload.today.date
                      ? "bg-primary"
                      : "bg-muted-foreground/70",
                )}
              />
            ) : (
              <span className="mx-auto mt-1 block size-1.5" />
            )}
          </button>
        ))}
      </div>

      {dayPhotos.length > 0 ? (
        <DiaryPhotoBubbles
          photos={dayPhotos}
          onOpenPhoto={openDayPhoto}
        />
      ) : null}

      {day.schoolDay && day.lessons.length > 0 ? (
        <ol className="space-y-3">
          {attached.byLesson.map(({ lesson, tasks: lessonTasks }) => (
            <li key={lesson.slotOrder} className="border-t border-foreground/8 pt-3 first:border-t-0 first:pt-0">
              <div className="flex items-baseline justify-between gap-3">
                {staff ? (
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-baseline justify-between gap-3 text-left"
                    onClick={() => {
                      setEditingTask(null);
                      setAddingLesson(lesson);
                    }}
                  >
                    <span className={cn("text-sm font-medium", currentSlot === lesson.slotOrder && isToday && now?.kind === "lesson" && "font-semibold")}>
                      {lesson.slotOrder}. {lesson.title}
                    </span>
                    <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                      {compactHhmm(lesson.start)}–{compactHhmm(lesson.end)}
                    </span>
                  </button>
                ) : (
                  <>
                    <p className={cn("text-sm font-medium", currentSlot === lesson.slotOrder && isToday && now?.kind === "lesson" && "font-semibold")}>
                      {lesson.slotOrder}. {lesson.title}
                    </p>
                    <p className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                      {compactHhmm(lesson.start)}–{compactHhmm(lesson.end)}
                    </p>
                  </>
                )}
              </div>
              {lessonTasks.length > 0 ? (
                <>
                  <DiaryTaskList
                    className="mt-1.5"
                    tasks={lessonTasks}
                    showPhoto={false}
                    onOpenPhoto={openDayPhoto}
                    onEdit={staff ? (item) => {
                      setAddingLesson(null);
                      setEditingTask(item);
                    } : undefined}
                  />
                  {staff ? (
                    <button
                      type="button"
                      className="mt-1 text-left text-xs font-medium text-primary"
                      onClick={() => {
                        setEditingTask(null);
                        setAddingLesson(lesson);
                      }}
                    >
                      Додати ще
                    </button>
                  ) : null}
                </>
              ) : staff ? (
                <button
                  type="button"
                  className="mt-1 text-left text-xs font-medium text-primary"
                  onClick={() => {
                    setEditingTask(null);
                    setAddingLesson(lesson);
                  }}
                >
                  Додати завдання
                </button>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">не задано</p>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-muted-foreground">{day.offReason ?? "Уроків немає."}</p>
      )}

      {attached.extra.length > 0 ? (
        <div>
          <p className="text-xs font-medium text-muted-foreground">Інше</p>
          <DiaryTaskList
            className="mt-2"
            tasks={attached.extra}
            showPhoto={false}
            onOpenPhoto={openDayPhoto}
            onEdit={staff ? setEditingTask : undefined}
          />
        </div>
      ) : null}

      <div>
        <SmoothReveal open={!editing}>
          <div className="space-y-2">
            <button
              type="button"
              className="block w-full text-center text-xs font-medium text-primary"
              onClick={() => setWeekOpen((open) => !open)}
              aria-expanded={weekOpen}
            >
              {weekOpen ? "Сховати таблицю тижня" : "Таблиця тижня"}
            </button>
            <SmoothReveal open={weekOpen}>
              <WeekGrid
                bells={payload.bells}
                lessons={payload.lessons}
                todayWeekday={payload.today.weekday}
                currentSlot={payload.today.schoolDay ? currentSlot : null}
                selectedWeekday={day.weekday}
              />
            </SmoothReveal>
            {staff ? (
              <button
                type="button"
                className="block w-full text-center text-xs font-medium text-primary"
                onClick={() => setEditing(true)}
              >
                Правити розклад
              </button>
            ) : null}
          </div>
        </SmoothReveal>
        <SmoothReveal open={editing}>
          <div className="pt-2">
            <TimetableEditor onDone={() => setEditing(false)} />
          </div>
        </SmoothReveal>
      </div>

      {openedPhoto && typeof document !== "undefined"
        ? createPortal(
            <ZoomPhotoDialog
              photos={openedPhoto.photos}
              startIndex={openedPhoto.index}
              alt={openedPhoto.alt}
              onClose={() => setOpenedPhoto(null)}
            />,
            document.body,
          )
        : null}

      <HomeworkEditDialog
        task={editingTask}
        create={addingLesson ? { subject: addingLesson.title, dueDate: day.date, slotOrder: addingLesson.slotOrder } : null}
        open={Boolean(editingTask) || Boolean(addingLesson)}
        onClose={() => {
          setEditingTask(null);
          setAddingLesson(null);
        }}
        onSaved={() => void load(selectedDate ?? undefined)}
      />
      {staff ? (
        <HomeworkCapturePanel
          open={assignOpen}
          onOpenChange={setAssignOpen}
          existingTasks={assignedToday}
          onSaved={() => void load(selectedDate ?? undefined)}
        />
      ) : null}
    </section>
  );
}

function WeekGrid({
  bells,
  lessons,
  todayWeekday,
  currentSlot,
  selectedWeekday,
}: {
  bells: TimetableResponse["bells"];
  lessons: LessonDto[];
  todayWeekday: number;
  currentSlot: number | null;
  selectedWeekday: number;
}) {
  const titleAt = useMemo(() => {
    const map = new Map<string, string>();
    for (const lesson of lessons) {
      map.set(`${lesson.weekday}:${lesson.slotOrder}`, lesson.title);
    }
    return map;
  }, [lessons]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed border-separate border-spacing-0 text-center">
        <thead>
          <tr>
            <th className="w-9 pb-1" />
            {[1, 2, 3, 4, 5].map((weekday) => (
              <th
                key={weekday}
                className={cn(
                  "px-0.5 pb-1 text-[11px] font-semibold text-muted-foreground",
                  weekday === selectedWeekday && "text-foreground",
                )}
              >
                {WEEKDAY_SHORT[weekday]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bells.map((bell) => (
            <tr key={bell.sortOrder}>
              <th
                className={cn(
                  "w-9 py-1 pr-1.5 text-left align-top text-[9px] font-medium leading-tight text-muted-foreground",
                  currentSlot === bell.sortOrder && "text-foreground",
                )}
              >
                <span className="block text-[10px] font-semibold text-foreground">{bell.sortOrder}</span>
                <span className="block tabular-nums">{compactHhmm(bell.start)}</span>
                <span className="block tabular-nums">{compactHhmm(bell.end)}</span>
              </th>
              {[1, 2, 3, 4, 5].map((weekday) => {
                const title = titleAt.get(`${weekday}:${bell.sortOrder}`) ?? "";
                const todayCell = weekday === todayWeekday && currentSlot === bell.sortOrder;
                return (
                  <td
                    key={weekday}
                    className={cn(
                      "border-t border-foreground/8 px-0.5 py-1.5 text-[11px] leading-snug",
                      weekday === selectedWeekday && "bg-accent/50",
                      todayCell && "bg-primary/15 font-semibold",
                      !title && "text-muted-foreground/50",
                    )}
                  >
                    {title ? shortSubject(title) : "—"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
